// src/contexts/UserContext.js (ou src/components/UserContext.js)

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getFirestore, getDoc } from 'firebase/firestore';

// 1. Créez le Contexte
const UserContext = createContext();

// 2. Hook personnalisé pour consommer le contexte facilement
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser doit être utilisé à l\'intérieur d\'un UserProvider');
  }
  return context;
};

// 3. Fournisseur de Contexte
export const UserProvider = ({ children }) => {
  const [userAuth, setUserAuth] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [errorLoadingUser, setErrorLoadingUser] = useState(null);

  const refreshUserProfile = useCallback(async () => {
    if (!userAuth) {
      setUserProfile(null);
      setIsLoadingUser(false);
      return;
    }

    setIsLoadingUser(true);
    const firestore = getFirestore();
    const userDocRef = doc(firestore, 'users', userAuth.uid);
    try {
      const docSnap = await getDoc(userDocRef);
      if (docSnap.exists()) {
        const profileData = docSnap.data();
        let schoolName = null;
        let schoolLogoUrl = null;

        // ✅ NOUVEAU : Récupère les détails de l'école si un schoolId est présent
        if (profileData.schoolId) {
          try {
            const schoolDocRef = doc(firestore, "schools", profileData.schoolId);
            const schoolSnap = await getDoc(schoolDocRef);
            if (schoolSnap.exists()) {
              const schoolData = schoolSnap.data();
              schoolName = schoolData.name || null;
              schoolLogoUrl = schoolData.logoUrl || null;
            } else {
              console.warn(`Détails de l'école non trouvés pour schoolId: ${profileData.schoolId}`);
            }
          } catch (err) {
            console.error("Erreur lors de la récupération des détails de l'école:", err);
            // On ne définit pas errorLoadingUser pour cette erreur secondaire,
            // pour ne pas bloquer l'accès si le profil utilisateur lui-même est valide.
          }
        }

        setUserProfile({
          uid: userAuth.uid,
          ...profileData,
          email: userAuth.email, // L'email de l'auth est toujours la source fiable
          profileCompleted: profileData.profileCompleted === true, // Force à true/false booléen
          schoolName: schoolName, // ✅ Utilise le nom de l'école récupéré
          schoolLogoUrl: schoolLogoUrl, // ✅ Utilise l'URL du logo récupéré
        });

      } else {
        // Le document utilisateur n'existe pas. On assume un profil incomplet pour l'instant.
        setUserProfile({
          uid: userAuth.uid,
          profileCompleted: false, // L'utilisateur doit compléter son profil
          role: 'unassigned', // Rôle par défaut pour un nouvel utilisateur sans profil Firestore
          schoolId: null,
          schoolName: null,
          schoolLogoUrl: null,
          email: userAuth.email,
        });
        console.warn("Document utilisateur Firestore manquant pour l'UID:", userAuth.uid, ". Le profil doit être complété.");
      }
      setErrorLoadingUser(null);
    } catch (error) {
      console.error("Erreur lors du rafraîchissement du profil utilisateur:", error);
      setErrorLoadingUser(error);
      setUserProfile(null);
    } finally {
      setIsLoadingUser(false);
    }
  }, [userAuth]); // Dépend de userAuth pour déclencher le rafraîchissement

  useEffect(() => {
    const auth = getAuth();
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      setUserAuth(user);
      if (user) {
        await refreshUserProfile();
      } else {
        setUserProfile(null);
        setErrorLoadingUser(null);
        setIsLoadingUser(false);
      }
    });

    return () => unsubscribeAuth(); // Nettoyage de l'abonnement
  }, [refreshUserProfile]); // Dépend du refreshUserProfile pour réagir à ses changements (bien que useCallback le stabilise)

  const value = {
    userAuth,
    userProfile,
    isLoadingUser,
    errorLoadingUser,
    refreshUserProfile, // Permet aux composants enfants de déclencher un rechargement
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
